'use client';

import React, { useState, useMemo } from 'react';
import { toast } from 'sonner';
import { Search, Plus, Building2, Users, Monitor, ChevronRight, MapPin, FileText, Edit2, Trash2, X,  } from 'lucide-react';
import { CLIENTS, EQUIPMENT, INTERVENTIONS, type Client } from '@/lib/data';
import ClientDetailPanel from './ClientDetailPanel';
import ClientForm from './ClientForm';
import ConfirmModal from '@/components/ui/ConfirmModal';

export default function ClientManagementScreen() {
  const [clients, setClients] = useState<Client[]>(CLIENTS);
  const [search, setSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Client | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sortKey, setSortKey] = useState<'name' | 'city' | 'pib'>('name');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return clients
      .filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.pib.includes(q) ||
        c.city.toLowerCase().includes(q) ||
        c.responsiblePerson.toLowerCase().includes(q)
      )
      .sort((a, b) => {
        const av = a[sortKey].toLowerCase();
        const bv = b[sortKey].toLowerCase();
        return sortDir === 'asc' ? av.localeCompare(bv, 'sr') : bv.localeCompare(av, 'sr');
      });
  }, [clients, search, sortKey, sortDir]);

  const selectedClient = clients.find(c => c.id === selectedClientId) ?? null;

  function toggleSort(key: typeof sortKey) {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  }

  function handleSaveClient(data: Client) {
    // Backend integration: POST /api/clients or PUT /api/clients/:id
    if (editingClient) {
      setClients(prev => prev.map(c => c.id === data.id ? data : c));
      toast.success('Klijent je uspešno ažuriran.');
    } else {
      setClients(prev => [...prev, data]);
      toast.success('Klijent je uspešno dodat.');
    }
    setFormOpen(false);
    setEditingClient(null);
  }

  function handleDelete() {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    // Backend integration: DELETE /api/clients/:id
    setTimeout(() => {
      setClients(prev => prev.filter(c => c.id !== deleteTarget.id));
      if (selectedClientId === deleteTarget.id) setSelectedClientId(null);
      toast.success(`Klijent "${deleteTarget.name}" je obrisan.`);
      setDeleteTarget(null);
      setDeleteLoading(false);
    }, 600);
  }

  const SortArrow = ({ k }: { k: typeof sortKey }) => (
    <span className="ml-1 inline-block" style={{ color: sortKey === k ? 'hsl(var(--accent))' : 'hsl(var(--muted-foreground))' }}>
      {sortKey === k ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
    </span>
  );

  return (
    <div className="flex h-full" style={{ minHeight: '100vh' }}>
      {/* Main content */}
      <div className={`flex flex-col flex-1 min-w-0 transition-all duration-300 ${selectedClient ? 'xl:max-w-none' : ''}`}>
        {/* Page header */}
        <div
          className="flex items-center justify-between px-6 py-5 border-b sticky top-0 z-10"
          style={{ backgroundColor: 'hsl(var(--card))', borderColor: 'hsl(var(--border))' }}
        >
          <div>
            <h1 className="text-2xl font-semibold" style={{ color: 'hsl(var(--foreground))' }}>
              Klijenti
            </h1>
            <p className="text-sm mt-0.5" style={{ color: 'hsl(var(--muted-foreground))' }}>
              {clients.length} {clients.length === 1 ? 'klijent' : clients.length < 5 ? 'klijenta' : 'klijenata'} ukupno
            </p>
          </div>
          <button
            className="btn-primary"
            onClick={() => { setEditingClient(null); setFormOpen(true); }}
          >
            <Plus size={16} />
            Dodaj klijenta
          </button>
        </div>

        {/* Search */}
        <div className="px-6 py-4 border-b" style={{ borderColor: 'hsl(var(--border))' }}>
          <div className="relative max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'hsl(var(--muted-foreground))' }} />
            <input
              type="text"
              placeholder="Pretraži po nazivu, PIB-u, gradu..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="input-field pl-9 pr-9"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2"
                style={{ color: 'hsl(var(--muted-foreground))' }}
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[700px]">
            <thead
              className="sticky top-0 z-[5]"
              style={{ backgroundColor: 'hsl(var(--muted) / 0.6)', borderBottom: '1px solid hsl(var(--border))' }}
            >
              <tr>
                <th
                  className="table-header"
                  onClick={() => toggleSort('name')}
                >
                  Naziv firme <SortArrow k="name" />
                </th>
                <th
                  className="table-header"
                  onClick={() => toggleSort('pib')}
                >
                  PIB <SortArrow k="pib" />
                </th>
                <th
                  className="table-header"
                  onClick={() => toggleSort('city')}
                >
                  Grad <SortArrow k="city" />
                </th>
                <th className="table-header">Odgovorno lice</th>
                <th className="table-header text-center">Zaposleni</th>
                <th className="table-header text-center">Oprema</th>
                <th className="table-header text-center">Intervencije</th>
                <th className="table-header"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-3">
                      <Building2 size={36} style={{ color: 'hsl(var(--muted-foreground))' }} />
                      <p className="font-medium" style={{ color: 'hsl(var(--foreground))' }}>
                        {search ? 'Nema rezultata pretrage' : 'Nema klijenata'}
                      </p>
                      <p className="text-sm" style={{ color: 'hsl(var(--muted-foreground))' }}>
                        {search ? `Nije pronađen klijent za "${search}"` : 'Dodajte prvog klijenta klikom na dugme iznad.'}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filtered.map(client => {
                  const eqCount = EQUIPMENT.filter(e => e.clientId === client.id).length;
                  const intCount = INTERVENTIONS.filter(i => i.clientId === client.id).length;
                  const isSelected = selectedClientId === client.id;
                  return (
                    <tr
                      key={client.id}
                      className={`table-row cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                      onClick={() => setSelectedClientId(isSelected ? null : client.id)}
                    >
                      <td className="table-cell">
                        <div className="flex items-center gap-2.5">
                          <div
                            className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold"
                            style={{ backgroundColor: 'hsl(var(--accent) / 0.12)', color: 'hsl(var(--accent))' }}
                          >
                            {client.name.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>
                              {client.name}
                            </p>
                            <p className="text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                              {client.address}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="table-cell">
                        <span className="mono text-xs" style={{ color: 'hsl(var(--muted-foreground))' }}>
                          {client.pib}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className="flex items-center gap-1 text-sm">
                          <MapPin size={12} style={{ color: 'hsl(var(--muted-foreground))' }} />
                          {client.city}
                        </span>
                      </td>
                      <td className="table-cell">
                        <span className="text-sm">{client.responsiblePerson}</span>
                      </td>
                      <td className="table-cell text-center">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold"
                          style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--foreground))' }}
                        >
                          <Users size={11} />
                          {client.employees.length}
                        </span>
                      </td>
                      <td className="table-cell text-center">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold"
                          style={{ backgroundColor: 'hsl(var(--muted))', color: 'hsl(var(--foreground))' }}
                        >
                          <Monitor size={11} />
                          {eqCount}
                        </span>
                      </td>
                      <td className="table-cell text-center">
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold"
                          style={{ backgroundColor: 'hsl(217 91% 60% / 0.10)', color: 'hsl(var(--accent))' }}
                        >
                          <FileText size={11} />
                          {intCount}
                        </span>
                      </td>
                      <td className="table-cell">
                        <div
                          className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            className="btn-ghost p-1.5"
                            title="Uredi klijenta"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditingClient(client);
                              setFormOpen(true);
                            }}
                          >
                            <Edit2 size={15} />
                          </button>
                          <button
                            className="btn-ghost p-1.5"
                            title="Obriši klijenta"
                            style={{ color: 'hsl(var(--destructive))' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteTarget(client);
                            }}
                          >
                            <Trash2 size={15} />
                          </button>
                          <ChevronRight
                            size={15}
                            style={{ color: isSelected ? 'hsl(var(--accent))' : 'hsl(var(--muted-foreground))' }}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {selectedClient && (
        <ClientDetailPanel
          client={selectedClient}
          equipment={EQUIPMENT.filter(e => e.clientId === selectedClient.id)}
          interventions={INTERVENTIONS.filter(i => i.clientId === selectedClient.id)}
          onClose={() => setSelectedClientId(null)}
          onEdit={(c) => { setEditingClient(c); setFormOpen(true); }}
        />
      )}

      {/* Form modal */}
      {formOpen && (
        <ClientForm
          open={formOpen}
          onClose={() => { setFormOpen(false); setEditingClient(null); }}
          onSave={handleSaveClient}
          initial={editingClient}
        />
      )}

      {/* Delete confirm */}
      <ConfirmModal
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Obriši klijenta"
        message={`Da li ste sigurni da želite da obrišete klijenta "${deleteTarget?.name}"? Ova akcija se ne može poništiti.`}
        confirmLabel="Obriši klijenta"
        loading={deleteLoading}
      />
    </div>
  );
}